import { Button, List, Table, Typography } from 'antd';
import { AimOutlined, CameraOutlined, FilePdfOutlined, FileSearchOutlined } from '@ant-design/icons';
import type { RegistryCase } from '../../../api/registryRecordApi';
import PhaseShell, { SectionCard, StatChip } from '../../../components/registry/PhaseShell';
import type { PhasePageProps } from '../../../components/registry/RegistryPhaseWorkspace';
import {
  SectionEmpty,
  groupBySourceDocument,
  openSourcePdf,
  registryDate as date,
  registryLabel as label,
  renderDocGroups,
} from '../../../components/registry/registryDocGroups';
import { imagingDocuments } from './phases';

const { Text } = Typography;

interface HpRow {
  caseCode: string;
  hp: NonNullable<RegistryCase['histopathology']>;
}

/** Detaliile HP prezente (margini, Breslow, IHC) — fără câmpurile lipsă. */
const hpDetails = (hp: NonNullable<RegistryCase['histopathology']>): string =>
  [
    hp.margins && `margini ${label(hp.margins)}`,
    hp.breslowMm != null && `Breslow ${hp.breslowMm} mm`,
    hp.topographyIcd10 && `topografie ${hp.topographyIcd10}`,
    hp.ihc && `IHC: ${hp.ihc}`,
  ]
    .filter(Boolean)
    .join(' · ');

/** Faza 2 — Screening & Diagnostic AI: FotoFinder/dermatoscopie, leziuni, histopatologie. */
export default function Faza2Page({ record, status }: PhasePageProps) {
  const imaging = imagingDocuments(record);
  const hpRows: HpRow[] = record.cases
    .filter((c) => c.histopathology)
    .map((c) => ({ caseCode: c.code, hp: c.histopathology! }));

  return (
    <PhaseShell
      title="Faza 2 — Screening & Diagnostic AI"
      description="Screeningul și confirmarea diagnosticului (evaluare imagistică FotoFinder, histopatologie)."
      status={status}
      chips={
        <>
          <StatChip count={imaging.length} label="documente imagistice" />
          <StatChip count={record.cases.length} label="leziuni" />
          <StatChip count={hpRows.length} label="buletine HP" />
        </>
      }
    >
      <SectionCard icon={<CameraOutlined />} title="Evaluare imagistică (FotoFinder / dermatoscopie)">
        {imaging.length === 0 ? (
          <SectionEmpty />
        ) : (
          <List
            size="small"
            dataSource={imaging}
            renderItem={(d) => (
              <List.Item
                actions={[
                  <Button key="pdf" size="small" type="link" icon={<FilePdfOutlined />} onClick={() => openSourcePdf(d.fileKey)}>
                    Vezi PDF
                  </Button>,
                ]}
              >
                <Text>
                  {d.fileName} <Text type="secondary">· {label(d.kind)} · {date(d.documentDate)}</Text>
                </Text>
              </List.Item>
            )}
          />
        )}
      </SectionCard>

      <SectionCard icon={<AimOutlined />} title="Leziuni evaluate">
        {record.cases.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(record.cases, record.documents, (c) => c.sourceDocumentId),
            (cases) => (
              <Table<RegistryCase>
                size="small"
                rowKey={(c) => c.lesionId ?? c.code}
                pagination={false}
                dataSource={cases}
                columns={[
                  { title: 'Cod', dataIndex: 'code', key: 'code' },
                  { title: 'Localizare', dataIndex: 'location', key: 'loc', render: (v: string | null | undefined) => v ?? '—' },
                  { title: 'ICD-10 prezumtiv', dataIndex: 'presumptiveIcd10', key: 'icd', render: (v: string | null | undefined) => v ?? '—' },
                ]}
              />
            ),
          )
        )}
      </SectionCard>

      <SectionCard icon={<FileSearchOutlined />} title="Histopatologie">
        {hpRows.length === 0 ? (
          <SectionEmpty />
        ) : (
          renderDocGroups(
            groupBySourceDocument(hpRows, record.documents, (r) => r.hp.sourceDocumentId),
            (rows) => (
              <Table<HpRow>
                size="small"
                rowKey={(r) => `${r.caseCode}-${r.hp.bulletinNo}`}
                pagination={false}
                dataSource={rows}
                columns={[
                  { title: 'Caz', dataIndex: 'caseCode', key: 'c' },
                  { title: 'Buletin', key: 'b', render: (_, r) => r.hp.bulletinNo ?? label(r.hp.status) },
                  { title: 'Data rezultatului', key: 'd', render: (_, r) => date(r.hp.resultDate) },
                  { title: 'Concluzie', key: 'concl', render: (_, r) => r.hp.conclusion ?? '—' },
                  { title: 'Detalii', key: 'det', render: (_, r) => hpDetails(r.hp) || '—' },
                ]}
              />
            ),
          )
        )}
      </SectionCard>
    </PhaseShell>
  );
}
